// /charges — listagem de cobranças com abas Todas/Recorrentes/Avulsas (Story 4.4).
// 4.10 vai estender com detalhe expandido + filtros avançados + retry button.

import {
  Banknote,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Link2,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatBR, parseLocalDate } from '@control-center/shared';
import type { ChargeListItem } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { PageHelp } from '../../components/common/PageHelp';
import { MarkPaidManualModal } from '../../components/charges/MarkPaidManualModal';
import { UpdateDueDateModal } from '../../components/charges/UpdateDueDateModal';
import {
  CHARGE_STATUS_DOT,
  CHARGE_STATUS_LABEL,
  chargeTypeLabel,
  canMarkPaidManual,
  canRetry,
  canUpdateDueDate,
  formatAmountBR,
} from '../../components/charges/charge-display';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { useCharges, useRetryFailedCharge } from '../../hooks/use-charges';
import { extractErrorMessage } from '../../lib/errors';

type Scope = 'todas' | 'recorrentes' | 'avulsas';

const TAB_DEFS: { value: Scope; label: string }[] = [
  { value: 'todas', label: 'Todas' },
  { value: 'recorrentes', label: 'Recorrentes' },
  { value: 'avulsas', label: 'Avulsas' },
];

function parseScope(raw: string | null): Scope {
  if (raw === 'recorrentes' || raw === 'avulsas') return raw;
  return 'todas';
}

/** Story 4.14 — copia o link do checkout interno (cobrança aguardando pagamento). */
function copyCheckoutLink(chargeId: string): void {
  const url = `${window.location.origin}/checkout/cobranca/${chargeId}`;
  void navigator.clipboard.writeText(url).then(
    () => toast.success('Link de pagamento copiado — envie ao cliente'),
    () => toast.error('Não foi possível copiar o link'),
  );
}

interface ChargesTableProps {
  hasFilters: boolean;
  onCreateNew: () => void;
  items: ChargeListItem[];
  onMarkPaid: (charge: ChargeListItem) => void;
  onUpdateDueDate: (charge: ChargeListItem) => void;
  onRetry: (charge: ChargeListItem) => void;
  onSelectCharge: (charge: ChargeListItem) => void;
  retryingId: string | null;
}

function ChargesTable({
  hasFilters,
  onCreateNew,
  items,
  onMarkPaid,
  onUpdateDueDate,
  onRetry,
  onSelectCharge,
  retryingId,
}: ChargesTableProps): JSX.Element {
  if (items.length === 0) {
    if (hasFilters) {
      return (
        <div className="rounded-[10px] border border-line bg-panel p-12 text-center">
          <Banknote size={24} strokeWidth={1.5} className="mx-auto mb-3 text-muted-3" />
          <p className="text-[13px] text-muted">Nenhuma cobrança bate com esses filtros.</p>
        </div>
      );
    }
    return (
      <RichEmptyState
        icon={Banknote}
        title="Sem cobranças geradas"
        description="Cobranças são disparadas no Asaas e o cliente recebe link pra pagar via Pix, boleto ou cartão."
        bullets={[
          'Avulsa — uma vez só (implantação, consultoria)',
          'Recorrente — sai automaticamente da assinatura',
          'Link enviado por e-mail (Resend) e WhatsApp (Evolution)',
        ]}
        primaryAction={{
          label: 'Nova cobrança avulsa',
          icon: <Plus size={14} strokeWidth={2} />,
          onClick: onCreateNew,
        }}
        secondaryAction={{
          label: 'Criar assinatura recorrente',
          href: '/subscriptions/new',
        }}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead className="border-b border-line bg-panel-2 text-[11px] font-medium uppercase tracking-wider text-muted-3">
          <tr>
            <th className="px-4 py-3 text-left">Criada</th>
            <th className="px-4 py-3 text-left">Cliente</th>
            <th className="px-4 py-3 text-left">Tipo</th>
            <th className="px-4 py-3 text-right">Valor</th>
            <th className="px-4 py-3 text-left">Vencimento</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Link</th>
            <th className="px-4 py-3 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr
              key={c.id}
              onClick={() => onSelectCharge(c)}
              className="cursor-pointer border-b border-line last:border-0 hover:bg-panel-2"
            >
              <td className="px-4 py-3 text-muted tnum">{formatBR(new Date(c.createdAt))}</td>
              <td className="px-4 py-3 text-ink">{c.clientName}</td>
              <td className="px-4 py-3 text-muted">{chargeTypeLabel(c)}</td>
              <td className="px-4 py-3 text-right tnum text-ink">{formatAmountBR(c.amount)}</td>
              <td className="px-4 py-3 text-muted tnum">{formatBR(parseLocalDate(c.dueDate))}</td>
              <td className="px-4 py-3">
                <span className="inline-flex items-center gap-[6px]">
                  <span
                    aria-hidden
                    className={cn(
                      'inline-block h-[8px] w-[8px] rounded-full ring-2',
                      CHARGE_STATUS_DOT[c.status] ?? 'bg-muted-2 ring-muted-3/15',
                    )}
                  />
                  <span className="text-muted">{CHARGE_STATUS_LABEL[c.status] ?? c.status}</span>
                </span>
              </td>
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                {c.status === 'aguardando_pagamento' ? (
                  <button
                    type="button"
                    onClick={() => copyCheckoutLink(c.id)}
                    title="Copiar link do checkout pra enviar ao cliente"
                    className="inline-flex items-center gap-1 rounded-[6px] border border-line px-2 py-1 text-[11px] text-muted hover:border-line-2 hover:text-ink"
                  >
                    <Link2 size={12} strokeWidth={1.8} />
                    Copiar link
                  </button>
                ) : c.paymentLink ? (
                  <a
                    href={c.paymentLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] text-ink-2 hover:text-ink"
                  >
                    <ExternalLink size={12} strokeWidth={1.8} />
                    Pagar
                  </a>
                ) : (
                  <span className="text-muted-3">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-1">
                  {canRetry(c.status) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry(c);
                      }}
                      disabled={retryingId === c.id}
                      title="Reprocessar criação no Asaas"
                      className="inline-flex items-center gap-1 rounded-[6px] border border-bad/30 bg-bad/5 px-2 py-1 text-[11px] text-bad hover:border-bad/50 hover:bg-bad/10 disabled:opacity-50"
                    >
                      <RefreshCw
                        size={12}
                        strokeWidth={1.8}
                        className={retryingId === c.id ? 'animate-spin' : undefined}
                      />
                      {retryingId === c.id ? 'Reprocessando…' : 'Reprocessar'}
                    </button>
                  )}
                  {canUpdateDueDate(c.status) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateDueDate(c);
                      }}
                      title="Adiar vencimento (preserva juros/multa)"
                      className="inline-flex items-center gap-1 rounded-[6px] border border-line px-2 py-1 text-[11px] text-muted hover:border-warn/40 hover:bg-warn/5 hover:text-warn"
                    >
                      <CalendarClock size={12} strokeWidth={1.8} />
                      Adiar
                    </button>
                  )}
                  {canMarkPaidManual(c.status) ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkPaid(c);
                      }}
                      title="Marcar paga manualmente (PIX direto, TED, depósito)"
                      className="inline-flex items-center gap-1 rounded-[6px] border border-line px-2 py-1 text-[11px] text-muted hover:border-good/40 hover:bg-good/5 hover:text-good"
                    >
                      <Check size={12} strokeWidth={1.8} />
                      Marcar paga
                    </button>
                  ) : (
                    !canUpdateDueDate(c.status) &&
                    !canRetry(c.status) && <span className="text-muted-3 text-[11px]">—</span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChargesPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [markPaidCharge, setMarkPaidCharge] = useState<ChargeListItem | null>(null);
  const [updateDueDateCharge, setUpdateDueDateCharge] = useState<ChargeListItem | null>(null);
  const retryMutation = useRetryFailedCharge();
  const [retryingId, setRetryingId] = useState<string | null>(null);

  async function handleRetry(charge: ChargeListItem): Promise<void> {
    setRetryingId(charge.id);
    try {
      const result = await retryMutation.mutateAsync(charge.id);
      toast.success(`Cobrança reprocessada (tentativa ${result.attemptsCount})`, {
        description: result.paymentLink ? 'Link de pagamento disponível' : undefined,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao reprocessar cobrança'));
    } finally {
      setRetryingId(null);
    }
  }

  const scope = parseScope(searchParams.get('scope'));
  const pageSize = 20;

  // Filtros (story 4.10)
  const status = searchParams.get('status') ?? undefined;
  const dueFrom = searchParams.get('dueFrom') ?? undefined;
  const dueTo = searchParams.get('dueTo') ?? undefined;
  const hasFilters = Boolean(status || dueFrom || dueTo);

  const query = useMemo(
    () => ({ scope, page, pageSize, status, dueFrom, dueTo }),
    [scope, page, status, dueFrom, dueTo],
  );
  const listQ = useCharges(query);

  function setScope(next: Scope): void {
    setPage(1);
    if (next === 'todas') {
      searchParams.delete('scope');
    } else {
      searchParams.set('scope', next);
    }
    setSearchParams(searchParams, { replace: true });
  }

  function setFilter(key: 'status' | 'dueFrom' | 'dueTo', value: string): void {
    setPage(1);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams, { replace: true });
  }

  function clearFilters(): void {
    setPage(1);
    searchParams.delete('status');
    searchParams.delete('dueFrom');
    searchParams.delete('dueTo');
    setSearchParams(searchParams, { replace: true });
  }

  const total = listQ.data?.pagination.total ?? 0;
  const totalPages = listQ.data?.pagination.totalPages ?? 1;
  const items = listQ.data?.data ?? [];

  return (
    <div className="px-6 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-[20px] font-semibold tracking-[-0.005em]">Cobranças</h1>
            <p className="mt-1 text-[12px] text-muted">
              {total} {total === 1 ? 'cobrança' : 'cobranças'} no total
            </p>
          </div>
          <PageHelp id="charges" />
        </div>
        <PillButton solid onClick={() => navigate('/charges/new')}>
          <Plus size={14} strokeWidth={1.8} />
          Nova avulsa
        </PillButton>
      </header>

      <nav className="mb-4 flex items-center gap-1 border-b border-line">
        {TAB_DEFS.map((tab) => {
          const active = tab.value === scope;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => setScope(tab.value)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
                active ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink-2',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* Filtros (Story 4.10 AC1) */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[11px] text-muted-3">
          <span className="uppercase tracking-wider">Status</span>
          <select
            value={status ?? ''}
            onChange={(e) => setFilter('status', e.target.value)}
            className="h-9 rounded-[8px] border border-line bg-panel px-2 text-[12px] text-ink"
          >
            <option value="">Todos</option>
            <option value="prevista">Prevista</option>
            <option value="enviada">Enviada</option>
            <option value="paga">Paga</option>
            <option value="paga_manual">Paga (manual)</option>
            <option value="vencida">Vencida</option>
            <option value="failed">Falhou</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted-3">
          <span className="uppercase tracking-wider">Vencimento de</span>
          <input
            type="date"
            value={dueFrom ?? ''}
            onChange={(e) => setFilter('dueFrom', e.target.value)}
            className="h-9 rounded-[8px] border border-line bg-panel px-2 text-[12px] text-ink"
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-muted-3">
          <span className="uppercase tracking-wider">até</span>
          <input
            type="date"
            value={dueTo ?? ''}
            onChange={(e) => setFilter('dueTo', e.target.value)}
            className="h-9 rounded-[8px] border border-line bg-panel px-2 text-[12px] text-ink"
          />
        </label>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-9 rounded-[8px] border border-line px-3 text-[12px] text-muted hover:border-line-2 hover:text-ink"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {listQ.isPending ? (
        <div className="rounded-[10px] border border-line bg-panel p-12 text-center text-[13px] text-muted">
          Carregando…
        </div>
      ) : listQ.isError ? (
        <div className="rounded-[10px] border border-bad/30 bg-bad/5 p-6 text-[13px] text-bad">
          Erro ao carregar cobranças. Tente novamente.
        </div>
      ) : (
        <>
          <ChargesTable
            hasFilters={hasFilters}
            onCreateNew={() => navigate('/charges/new')}
            items={items}
            onMarkPaid={setMarkPaidCharge}
            onUpdateDueDate={setUpdateDueDateCharge}
            onRetry={handleRetry}
            onSelectCharge={(c) => navigate(`/charges/${c.id}`)}
            retryingId={retryingId}
          />

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2 text-[12px] text-muted">
              <span>
                Página {page} de {totalPages}
              </span>
              <PillButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={14} strokeWidth={1.8} />
              </PillButton>
              <PillButton
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight size={14} strokeWidth={1.8} />
              </PillButton>
            </div>
          )}
        </>
      )}

      <MarkPaidManualModal
        open={markPaidCharge !== null}
        onOpenChange={(open) => {
          if (!open) setMarkPaidCharge(null);
        }}
        charge={
          markPaidCharge
            ? {
                id: markPaidCharge.id,
                clientName: markPaidCharge.clientName,
                amount: markPaidCharge.amount,
                description: markPaidCharge.description,
              }
            : null
        }
      />

      <UpdateDueDateModal
        open={updateDueDateCharge !== null}
        onOpenChange={(open) => {
          if (!open) setUpdateDueDateCharge(null);
        }}
        charge={
          updateDueDateCharge
            ? {
                id: updateDueDateCharge.id,
                clientName: updateDueDateCharge.clientName,
                description: updateDueDateCharge.description,
                dueDate: updateDueDateCharge.dueDate,
              }
            : null
        }
      />
    </div>
  );
}
